import React, { useState } from 'react';

const InteractiveDemo = () => {
  const [activeDemo, setActiveDemo] = useState('maintenance');
  const [simulationRunning, setSimulationRunning] = useState(false);

  const demos = {
    maintenance: {
      title: "Maintenance Prédictive",
      description: "Simulateur de prédiction de pannes basé sur l'analyse des données véhicule",
      metrics: [
        { label: "Fiabilité Prédiction", value: "94.2%", color: "green" },
        { label: "Réduction Pannes", value: "68%", color: "blue" },
        { label: "Économies Générées", value: "€45K/an", color: "purple" }
      ],
      data: [
        { component: "Transmission", risk: 12, nextMaintenance: "3 semaines" },
        { component: "Freins", risk: 78, nextMaintenance: "Urgent" },
        { component: "Moteur", risk: 23, nextMaintenance: "2 mois" },
        { component: "Suspension", risk: 45, nextMaintenance: "1 mois" }
      ]
    },
    personalization: {
      title: "Personnalisation Client",
      description: "IA de recommandation basée sur le profil et l\'historique client",
      metrics: [
        { label: "Taux Conversion", value: "73%", color: "green" },
        { label: "Satisfaction Client", value: "+45%", color: "blue" },
        { label: "Ventes Additionnelles", value: "€12K/mois", color: "purple" }
      ],
      data: [
        { recommendation: "Assurance Premium", probability: 87, impact: "Haute" },
        { recommendation: "Service Étendu", probability: 65, impact: "Moyenne" },
        { recommendation: "Accessoires", probability: 92, impact: "Haute" },
        { recommendation: "Financement 0%", probability: 78, impact: "Haute" }
      ]
    },
    optimization: {
      title: "Optimisation Opérationnelle",
      description: "Algorithmes d\'optimisation pour la gestion des stocks et des processus",
      metrics: [
        { label: "Rotation Stock", value: "+120%", color: "green" },
        { label: "Temps Livraison", value: "-40%", color: "blue" },
        { label: "Coûts Stockage", value: "-35%", color: "purple" }
      ],
      data: [
        { process: "Approvisionnement", efficiency: 89, status: "Optimal" },
        { process: "Logistique", efficiency: 76, status: "Bon" },
        { process: "Distribution", efficiency: 94, status: "Excellent" },
        { process: "Service Client", efficiency: 82, status: "Bon" }
      ]
    }
  };

  const runSimulation = () => {
    setSimulationRunning(true);
    setTimeout(() => {
      setSimulationRunning(false);
    }, 2000);
  };

  const getRiskColor = (risk) => {
    if (risk < 30) return 'text-green-600 bg-green-100';
    if (risk < 70) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const getEfficiencyColor = (efficiency) => {
    if (efficiency >= 90) return 'text-green-600 bg-green-100';
    if (efficiency >= 75) return 'text-blue-600 bg-blue-100';
    return 'text-yellow-600 bg-yellow-100';
  };

  return (
    <section id="interactive-demo" className="py-16 bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-purple-100 text-purple-800 text-sm font-medium mb-4">
            <span className="w-2 h-2 bg-purple-500 rounded-full mr-2"></span>
            Démo Interactive
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Explorez l'IA en Action
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Testez nos solutions IA avec des simulations interactives basées sur des données réelles du secteur automobile africain
          </p>
        </div>

        {/* Demo Navigation */}
        <div className="flex flex-wrap justify-center gap-4 mb-8">
          {Object.entries(demos)?.map(([key, demo]) => (
            <button
              key={key}
              onClick={() => setActiveDemo(key)}
              className={`px-6 py-3 rounded-lg font-semibold transition-all duration-300 ${
                activeDemo === key
                  ? 'bg-purple-600 text-white shadow-lg'
                  : 'bg-white text-gray-700 hover:bg-purple-50 hover:text-purple-600 border border-gray-200'
              }`}
            >
              {demo?.title}
            </button>
          ))}
        </div>

        {/* Active Demo */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {/* Demo Header */}
          <div className="bg-gradient-to-r from-purple-600 to-blue-600 p-6 text-white">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
              <div>
                <h3 className="text-2xl font-bold mb-2">{demos?.[activeDemo]?.title}</h3>
                <p className="text-purple-100">{demos?.[activeDemo]?.description}</p>
              </div>
              <button
                onClick={runSimulation}
                disabled={simulationRunning}
                className="mt-4 md:mt-0 px-6 py-2 bg-white/20 backdrop-blur-sm border border-white/30 rounded-lg font-semibold hover:bg-white/30 transition-all duration-300 disabled:opacity-50"
              >
                {simulationRunning ? (
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Analyse...</span>
                  </div>
                ) : (
                  'Lancer Simulation'
                )}
              </button>
            </div>
          </div>

          {/* Demo Content */}
          <div className="p-6">
            {/* Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              {demos?.[activeDemo]?.metrics?.map((metric, index) => (
                <div key={index} className="text-center bg-gray-50 rounded-xl p-6">
                  <div className={`text-3xl font-bold mb-2 ${
                    metric?.color === 'green' ? 'text-green-600' :
                    metric?.color === 'blue' ? 'text-blue-600' : 'text-purple-600'
                  }`}>
                    {metric?.value}
                  </div>
                  <div className="text-gray-700 text-sm font-medium">{metric?.label}</div>
                </div>
              ))}
            </div>

            {/* Data Visualization */}
            <div className="space-y-4">
              {activeDemo === 'maintenance' && (
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">État des Composants Véhicule</h4>
                  {demos?.[activeDemo]?.data?.map((item, index) => (
                    <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg mb-3">
                      <div className="flex items-center space-x-4">
                        <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center">
                          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/>
                          </svg>
                        </div>
                        <div>
                          <div className="font-semibold text-gray-900">{item?.component}</div>
                          <div className="text-sm text-gray-600">Prochaine maintenance: {item?.nextMaintenance}</div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getRiskColor(item?.risk)}`}>
                          Risque: {item?.risk}%
                        </span>
                        <div className="w-24 bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full transition-all duration-1000 ${
                              item?.risk < 30 ? 'bg-green-500' : 
                              item?.risk < 70 ? 'bg-yellow-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${item?.risk}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {activeDemo === 'personalization' && (
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Recommandations Client Personnalisées</h4>
                  {demos?.[activeDemo]?.data?.map((item, index) => (
                    <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg mb-3">
                      <div className="flex items-center space-x-4">
                        <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                          </svg>
                        </div>
                        <div>
                          <div className="font-semibold text-gray-900">{item?.recommendation}</div>
                          <div className="text-sm text-gray-600">Impact: {item?.impact}</div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-semibold">
                          {item?.probability}% de probabilité
                        </span>
                        <div className="w-24 bg-gray-200 rounded-full h-2">
                          <div 
                            className="h-2 bg-green-500 rounded-full transition-all duration-1000"
                            style={{ width: `${item?.probability}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {activeDemo === 'optimization' && (
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Performance des Processus</h4>
                  {demos?.[activeDemo]?.data?.map((item, index) => (
                    <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg mb-3">
                      <div className="flex items-center space-x-4">
                        <div className="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center">
                          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
                          </svg>
                        </div>
                        <div>
                          <div className="font-semibold text-gray-900">{item?.process}</div>
                          <div className="text-sm text-gray-600">Statut: {item?.status}</div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getEfficiencyColor(item?.efficiency)}`}>
                          {item?.efficiency}% efficacité
                        </span>
                        <div className="w-24 bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full transition-all duration-1000 ${
                              item?.efficiency >= 90 ? 'bg-green-500' : 
                              item?.efficiency >= 75 ? 'bg-blue-500' : 'bg-yellow-500'
                            }`}
                            style={{ width: `${item?.efficiency}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="text-center mt-12">
          <div className="bg-white rounded-xl p-8 shadow-lg border border-purple-200">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              Intéressé par une Démonstration Personnalisée ?
            </h3>
            <p className="text-gray-600 mb-6">
              Découvrez comment ces solutions IA peuvent transformer votre entreprise automobile
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="px-8 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all duration-300">
                Demander une Démo
              </button>
              <button className="px-8 py-3 bg-white border-2 border-purple-600 text-purple-600 font-semibold rounded-lg hover:bg-purple-600 hover:text-white transition-all duration-300">
                Télécharger le Guide IA
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default InteractiveDemo;